import { useCallback, useRef, useState } from 'react';
import type { ManagerOverrideAuthorizedBy } from '@/lib/api/pin.js';

export type OverrideResult =
  | { approved: true; authorizedBy: ManagerOverrideAuthorizedBy }
  | { approved: false; authorizedBy?: null };

export type ManagerOverrideRequest = {
  open: boolean;
  action: string;
  title: string;
  description: string;
  requiredRole: 'store_manager' | 'tenant_admin';
  context?: {
    ticketId?: string;
    customerId?: string;
    amount?: number;
    percent?: number;
    reason?: string;
    [key: string]: unknown;
  };
};

/**
 * Imperative manager-override PIN prompt (same pattern as usePinVerification).
 * Render `<ManagerOverrideModal {...modalProps} />` when `modalProps` is non-null.
 */
export function useManagerOverride() {
  const [request, setRequest] = useState<ManagerOverrideRequest | null>(null);
  const resolverRef = useRef<((value: OverrideResult) => void) | null>(null);

  const requireOverride = useCallback(
    (params: {
      action: string;
      title: string;
      description: string;
      requiredRole?: 'store_manager' | 'tenant_admin';
      context?: ManagerOverrideRequest['context'];
    }): Promise<OverrideResult> => {
      return new Promise<OverrideResult>((resolve) => {
        resolverRef.current = resolve;
        setRequest({
          open: true,
          action: params.action,
          title: params.title,
          description: params.description,
          requiredRole: params.requiredRole ?? 'store_manager',
          ...(params.context ? { context: params.context } : {}),
        });
      });
    },
    [],
  );

  const handleApproved = useCallback((authorizedBy: ManagerOverrideAuthorizedBy) => {
    resolverRef.current?.({ approved: true, authorizedBy });
    resolverRef.current = null;
    setRequest(null);
  }, []);

  const handleClose = useCallback(() => {
    resolverRef.current?.({ approved: false });
    resolverRef.current = null;
    setRequest(null);
  }, []);

  const modalProps = request
    ? {
        open: request.open,
        onClose: handleClose,
        onApproved: handleApproved,
        action: request.action,
        title: request.title,
        description: request.description,
        requiredRole: request.requiredRole,
        ...(request.context ? { context: request.context } : {}),
      }
    : null;

  return { requireOverride, modalProps };
}
